import { DecimalPipe } from '@angular/common';
import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AutoGrowDirective } from './auto-grow.directive';
import { CaseExtraction, DocumentBatch, ExtractedCaseFields } from './document-ocr.models';
import { DocumentOcrService } from './document-ocr.service';
import { OpsService } from '../../core/services/ops.service';
import { AUTO_LINK_THRESHOLD, MATCH_TIER_LABEL, isLinkable, matchTier } from './match-tier';

type ReviewTab = 'details' | 'matches' | 'text';

const SUMMARY_FIELDS: ReadonlySet<keyof ExtractedCaseFields> = new Set(['summaryEnglish', 'summaryHindi']);

const EDITABLE_FIELDS: Array<{ key: keyof ExtractedCaseFields; label: string }> = [
  { key: 'applicantName', label: 'Applicant name' },
  { key: 'relativeName', label: "Father's / Husband's name" },
  { key: 'subjectEnglish', label: 'Subject (English)' },
  { key: 'subjectHindi', label: 'Subject (Hindi)' },
  { key: 'caseType', label: 'Case type' },
  { key: 'department', label: 'Department' },
  { key: 'category', label: 'Category' },
  { key: 'village', label: 'Village' },
  { key: 'tehsil', label: 'Tehsil' },
  { key: 'vikasKhand', label: 'Vikas Khand' },
  { key: 'thana', label: 'Thana' },
  { key: 'gramPanchayat', label: 'Gram Panchayat' },
  { key: 'mobileNo', label: 'Mobile' },
  { key: 'araziKhasraNo', label: 'Arazi / Khasra No.' },
  { key: 'summaryEnglish', label: 'Summary (English)' },
  { key: 'summaryHindi', label: 'Summary (Hindi)' },
];

@Component({
  selector: 'app-ocr-review',
  standalone: true,
  imports: [FormsModule, RouterLink, DecimalPipe, AutoGrowDirective],
  templateUrl: './ocr-review.component.html',
  styleUrl: './ocr-review.component.scss',
})
export class OcrReviewComponent implements OnInit {
  private readonly ocr = inject(DocumentOcrService);
  private readonly ops = inject(OpsService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly sanitizer = inject(DomSanitizer);

  readonly fields = EDITABLE_FIELDS.filter((f) => !SUMMARY_FIELDS.has(f.key));
  readonly summaryFields = EDITABLE_FIELDS.filter((f) => SUMMARY_FIELDS.has(f.key));
  readonly departments = computed(() => this.ops.departments);
  readonly extraction = signal<CaseExtraction | null>(null);
  readonly batch = signal<DocumentBatch | null>(null);
  readonly batchExtractions = signal<CaseExtraction[]>([]);
  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly message = signal<string | null>(null);
  readonly error = signal<string | null>(null);
  readonly selectedCandidate = signal<number | null>(null);
  readonly activeTab = signal<ReviewTab>('details');
  readonly matchTier = matchTier;
  readonly matchTierLabel = MATCH_TIER_LABEL;
  readonly isLinkable = isLinkable;

  readonly completedCount = computed(
    () => this.batchExtractions().filter((e) => e.grievanceRef != null || e.matchStatus === 'rejected').length,
  );
  readonly remainingCount = computed(() => this.batchExtractions().length - this.completedCount());

  readonly previewUrl = signal<string | null>(null);
  readonly previewKind = signal<'image' | 'pdf' | 'unsupported' | null>(null);
  /** iframe/embed src is a RESOURCE_URL sink in Angular's sanitizer — this blob URL is our own fetch, safe to trust. */
  readonly safeEmbedUrl = computed(() => {
    const url = this.previewUrl();
    return url ? this.sanitizer.bypassSecurityTrustResourceUrl(url) : null;
  });

  constructor() {
    this.destroyRef.onDestroy(() => {
      const url = this.previewUrl();
      if (url) URL.revokeObjectURL(url);
    });
  }

  values: Partial<Record<keyof ExtractedCaseFields, string>> = {};

  readonly isResolved = computed(() => this.extraction()?.grievanceRef != null);
  readonly isRejected = computed(() => this.extraction()?.matchStatus === 'rejected');

  async ngOnInit(): Promise<void> {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!id) {
      void this.router.navigate(['/document-ocr', 'documents']);
      return;
    }
    try {
      const extraction = await this.ocr.fetchExtraction(id);
      this.extraction.set(extraction);
      this.resetValuesFrom(extraction);
      const top = extraction.matchCandidates[0];
      // Only pre-select when it's a confident match — never nudge toward linking a weak guess.
      if (top && top.score >= AUTO_LINK_THRESHOLD) {
        this.selectedCandidate.set(top.grievanceId);
      }
      void this.loadPreview(extraction.id);
      void this.loadBatchContext(extraction.batchRef);
    } catch (err) {
      this.error.set(err instanceof Error ? err.message : 'Could not load this case');
    } finally {
      this.loading.set(false);
    }
  }

  /** Best-effort inline preview, cropped to just this extraction's own pages — the download
   *  button still works if this fails. */
  private async loadPreview(extractionId: number): Promise<void> {
    try {
      const { url, mimeType } = await this.ocr.getExtractionSourcePreview(extractionId);
      this.previewUrl.set(url);
      if (mimeType.startsWith('image/')) {
        this.previewKind.set('image');
      } else if (mimeType === 'application/pdf') {
        this.previewKind.set('pdf');
      } else {
        this.previewKind.set('unsupported');
      }
    } catch {
      this.previewKind.set('unsupported');
    }
  }

  /** Batch-level progress (pages / completed / remaining) — best-effort, the review still works without it. */
  private async loadBatchContext(batchId: number): Promise<void> {
    try {
      const result = await this.ocr.refreshOne(batchId);
      if (!result) return;
      this.batch.set(result.batch);
      this.batchExtractions.set(result.extractions);
    } catch {
      // Non-fatal — the stat bar just stays empty.
    }
  }

  async copyBatchId(): Promise<void> {
    const id = this.batch()?.id;
    if (id == null) return;
    try {
      await navigator.clipboard.writeText(String(id));
      this.message.set('Batch ID copied.');
    } catch {
      // Clipboard permission denied — nothing we can do, fail silently.
    }
  }

  async downloadSource(): Promise<void> {
    const current = this.extraction();
    if (!current) return;
    try {
      await this.ocr.downloadExtractionSourceFile(current.id, `case-${current.id}-source`);
    } catch (err) {
      this.error.set(err instanceof Error ? err.message : 'Could not download the original file');
    }
  }

  confidenceFor(key: keyof ExtractedCaseFields): number | null {
    const c = this.extraction()?.extractedJson.fieldConfidence?.[key as string];
    return typeof c === 'number' ? c : null;
  }

  async save(): Promise<void> {
    const current = this.extraction();
    if (!current) return;
    this.saving.set(true);
    this.error.set(null);
    try {
      const updated = await this.ocr.correctExtraction(current.id, this.values as Partial<ExtractedCaseFields>);
      this.extraction.set(updated);
      this.resetValuesFrom(updated);
      this.message.set('Corrections saved.');
    } catch (err) {
      this.error.set(err instanceof Error ? err.message : 'Could not save corrections');
    } finally {
      this.saving.set(false);
    }
  }

  async linkToSelected(): Promise<void> {
    const current = this.extraction();
    const grievanceRef = this.selectedCandidate();
    if (!current || grievanceRef == null) return;
    await this.confirm(current.id, 'link', grievanceRef);
  }

  async createNew(): Promise<void> {
    const current = this.extraction();
    if (!current) return;
    await this.confirm(current.id, 'new');
  }

  async reject(): Promise<void> {
    const current = this.extraction();
    if (!current) return;
    await this.confirm(current.id, 'reject');
  }

  private async confirm(extractionId: number, action: 'link' | 'new' | 'reject', grievanceRef?: number): Promise<void> {
    this.saving.set(true);
    this.error.set(null);
    try {
      const updated = await this.ocr.confirmExtraction(extractionId, action, grievanceRef);
      this.extraction.set(updated);
      this.message.set(
        action === 'reject'
          ? 'Case rejected.'
          : action === 'new'
            ? `Confirmed — created case #${updated.grievanceRef}.`
            : `Confirmed — linked to case #${updated.grievanceRef}.`,
      );
    } catch (err) {
      this.error.set(err instanceof Error ? err.message : 'Could not confirm this case');
    } finally {
      this.saving.set(false);
    }
  }

  private resetValuesFrom(extraction: CaseExtraction): void {
    const values: Partial<Record<keyof ExtractedCaseFields, string>> = {};
    for (const f of [...this.fields, ...this.summaryFields]) {
      const v = extraction.extractedJson[f.key];
      values[f.key] = v == null ? '' : String(v);
    }
    this.values = values;
  }
}
